"use client";

import { useParams, usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import posthog from "posthog-js";

import { isPostHogCapturing } from "@/lib/posthog/is-capturing";
import {
  buildNavigationSample,
  isIgnoredPath,
  NAVIGATION_TIMING_EVENT,
  routePattern,
  rscPathOf,
  selectRscEntry,
  type RawTimingEntry,
} from "@/lib/perf/navigation-timing";

/** Samples real-user navigation latency into PostHog.
 *
 *  Mounted once in the ROOT layout, deliberately not in `(authed)/layout.tsx`
 *  where `PostHogIdentify` lives. Two reasons: `/login`, `/signup`, `/legal/*`
 *  and `/onboarding` sit outside the `(authed)` group and are worth measuring,
 *  and the `(authed)` subtree unmounts when you navigate out of it, which
 *  would reset this component's refs mid-session.
 *
 *  Keys off `usePathname` so it records exactly the navigations a user waited
 *  on. Next prefetches on link hover/viewport, and a prefetch never changes
 *  the pathname. Filtering prefetches server-side is not possible; see
 *  `applyServerTiming` in `apps/web/proxy.ts` for the measured reason.
 *
 *  Not sampled down: roughly one event per real navigation, a few thousand a
 *  day at current traffic, and posthog-js batches it onto the `$pageview`
 *  request already in flight for the same navigation. */
export function NavigationTiming() {
  const pathname = usePathname();
  const params = useParams();

  /** The sampling effect deliberately depends on `pathname` ALONE, never on
   *  `params`. Next memoizes `useParams()` on the router tree
   *  (`app-router.js`: `useMemo(() => getSelectedParams(tree), [tree])`), and
   *  the tree is replaced on any router state update, including
   *  `router.refresh()` and Server Action `revalidatePath` (24 such call
   *  sites in this app). Depending on `params` would therefore re-fire the
   *  effect with no navigation having occurred, emitting a phantom
   *  zero-duration sample that inflates the `prefetched` bucket and drags
   *  p75 down on exactly the routes that use Server Actions.
   *
   *  `route` itself is safe to derive inline: it is a string, so even when
   *  the params object identity churns, the value is unchanged. */
  const route = routePattern(pathname, params);
  /** Mirror of `route` for the observer, whose callback fires outside any
   *  render. Written from the effect below, never during render. */
  const routeRef = useRef("");

  /** Latest RSC fetch seen per pathname.
   *
   *  Fed by a PerformanceObserver rather than read from
   *  `getEntriesByType("resource")` alone, because that buffer is capped at
   *  250 entries and then silently drops new ones. At this app's ~20:1
   *  prefetch-to-navigation ratio the cap is reached after roughly a dozen
   *  navigations, after which every real navigation would find no entry and
   *  be misrecorded as a zero-cost `prefetched` hit. That failure is
   *  indistinguishable from the genuine fast case, so it would not surface as
   *  missing data: it would quietly inflate the `prefetched` share and leave
   *  the `rsc` bucket holding only the first few navigations of each session.
   *  Observers receive entries independently of the buffer cap. */
  const observedRef = useRef<Map<string, RawTimingEntry>>(new Map());
  /** End time of the last recorded navigation, so a stale prefetch of the
   *  same route is never attributed to this one. */
  const sinceRef = useRef(0);
  /** The first pathname effect corresponds to the hard load, which the
   *  observer reports instead (see below). */
  const skipFirstRef = useRef(true);

  useEffect(() => {
    if (typeof PerformanceObserver === "undefined") return;
    const origin = window.location.origin;
    const observed = observedRef.current;

    const emit = (entry: RawTimingEntry | null, hardLoad: boolean) => {
      const route = routeRef.current;
      if (!route || isIgnoredPath(route)) return;
      if (!isPostHogCapturing()) return;
      posthog.capture(
        NAVIGATION_TIMING_EVENT,
        buildNavigationSample({ route, hardLoad, entry }),
      );
    };

    const observer = new PerformanceObserver((list) => {
      for (const raw of list.getEntries()) {
        const entry = raw as unknown as RawTimingEntry;
        if (raw.entryType === "navigation") {
          // The hard page load. Reported here rather than from the pathname
          // effect because `PerformanceNavigationTiming.duration` is
          // `loadEventEnd - startTime`, which is still 0 while React
          // hydration effects run. Reading it there saw 0 on the common
          // path, and the previous "bail and retry next time" handling then
          // attributed the DOCUMENT's timing to whatever route the user
          // navigated to next. The observer fires once the entry is
          // finalized, so the value is real and the route is still correct.
          if (entry.duration > 0) emit(entry, true);
          continue;
        }
        const path = rscPathOf(entry, origin);
        if (path) observed.set(path, entry);
      }
    });

    // Observed separately so a browser lacking one entry type still yields
    // the other; a combined observe() call would abort both.
    for (const type of ["navigation", "resource"] as const) {
      try {
        observer.observe({ type, buffered: true });
      } catch {
        // Telemetry is best-effort; never let it break the page.
      }
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof performance === "undefined") return;
    routeRef.current = route;

    // The hard load is the observer's job; this effect owns soft navigations
    // only. Prime `sinceRef` so the first soft navigation cannot match an
    // RSC entry that belongs to the initial page load.
    if (skipFirstRef.current) {
      skipFirstRef.current = false;
      sinceRef.current = performance.now();
      return;
    }

    if (isIgnoredPath(route)) return;
    if (!isPostHogCapturing()) return;

    const seen = observedRef.current.get(pathname);
    const entry =
      seen && seen.responseEnd >= sinceRef.current
        ? seen
        : // Fallback covers the window where the observer callback has not
          // yet delivered this navigation's entry.
          selectRscEntry(
            performance.getEntriesByType(
              "resource",
            ) as unknown as RawTimingEntry[],
            pathname,
            window.location.origin,
            sinceRef.current,
          );

    // Consumed: drop it so the map stays bounded by in-flight navigations
    // rather than by every distinct path visited in the session.
    observedRef.current.delete(pathname);

    sinceRef.current = performance.now();
    posthog.capture(
      NAVIGATION_TIMING_EVENT,
      buildNavigationSample({ route, hardLoad: false, entry }),
    );
    // `route` is a derived STRING, so including it is dependency-honest
    // without reintroducing the identity churn `params` would cause: a
    // Server Action revalidation recomputes the same value and React skips
    // the effect. `pathname` is read for the observed-entry lookup.
  }, [pathname, route]);

  return null;
}
